export class CalculatorPresentationContractError extends TypeError {
    constructor(path, message) {
        super(`calculator presentation contract failed at ${path}: ${message}`);
        this.name = "CalculatorPresentationContractError";
        this.path = path;
    }
}

export function requirePresentationRecord(value, path) {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        throw new CalculatorPresentationContractError(path, "expected an object");
    }
    return value;
}

export function requirePresentationFields(value, fields, path) {
    const record = requirePresentationRecord(value, path);
    const expected = [...fields].sort();
    const actual = Object.keys(record).sort();
    if (
        actual.length !== expected.length
        || actual.some((field, index) => field !== expected[index])
    ) {
        throw new CalculatorPresentationContractError(
            path,
            `fields must be exactly: ${expected.join(", ")}`,
        );
    }
    return record;
}

export function requirePresentationArray(value, path) {
    if (!Array.isArray(value)) {
        throw new CalculatorPresentationContractError(path, "expected an array");
    }
    return value;
}

export function requirePresentationString(value, path, { allowEmpty = false } = {}) {
    if (typeof value !== "string" || (!allowEmpty && value.length === 0)) {
        throw new CalculatorPresentationContractError(
            path,
            allowEmpty ? "expected a string" : "expected a non-empty string",
        );
    }
    return value;
}

export function requirePresentationFinite(value, path, { minimum = -Infinity, maximum = Infinity } = {}) {
    if (!Number.isFinite(value) || value < minimum || value > maximum) {
        throw new CalculatorPresentationContractError(
            path,
            `expected a finite number from ${minimum} through ${maximum}`,
        );
    }
    return value;
}

export function requirePresentationBoolean(value, path) {
    if (typeof value !== "boolean") {
        throw new CalculatorPresentationContractError(path, "expected a boolean");
    }
    return value;
}

export function requirePresentationNullableString(value, path) {
    return value === null
        ? null
        : requirePresentationString(value, path, { allowEmpty: false });
}

export function parseCalculatorSourceMainGroupPresentation(value, path) {
    const record = requirePresentationRecord(value, path);
    const state = requirePresentationString(record.state, `${path}.state`);
    if (state === "known") {
        const known = requirePresentationFields(record, ["id", "state"], path);
        if (!Number.isSafeInteger(known.id) || known.id <= 0) {
            throw new CalculatorPresentationContractError(
                `${path}.id`,
                "expected a positive safe integer",
            );
        }
        return Object.freeze({ state, id: known.id });
    }
    if (state === "unknown" || state === "unavailable") {
        requirePresentationFields(record, ["state"], path);
        return Object.freeze({ state });
    }
    throw new CalculatorPresentationContractError(
        `${path}.state`,
        "expected known, unknown, or unavailable",
    );
}
